/**
 * Queues = Arbeits-Postfaecher, in die Quellen einliefern (Spec 3.1/3.2).
 *
 * Name + Settings kommen aus Phase 1. Mit Phase 2 kommen `inbound_config` und
 * `outbound_config` fuer E-Mail-Queues dazu (Spec 3.2 / 6). Beide sind
 * **eigene Spalten**, nicht Teil von `settings`: sie enthalten verschluesselte
 * Credentials und werden nach aussen anders dargestellt als gespeichert
 * (siehe `schemas/email.ts`).
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
import { inboundConfigSchema, outboundConfigSchema } from './email.js';
import { ticketPrioritySchema } from './ticket.js';
/**
 * Auto-Frist je Prioritaet (Spec 8.3: „dringend = 4 h, wichtig = 1 Werktag").
 * `null` heisst „keine automatische Frist" — ein Agent kann `due_at` trotzdem
 * von Hand setzen. Gerechnet wird in vollen Stunden ab Ticket-Anlage; einen
 * Werktagskalender gibt es bewusst nicht (Einfachheit vor Skalierung).
 */
export const queueDueHoursSchema = z.object({
    normal: z.number().int().min(1).max(8760).nullable().default(null),
    important: z.number().int().min(1).max(8760).nullable().default(null),
    urgent: z.number().int().min(1).max(8760).nullable().default(null),
});
/**
 * Eskalationsregel je Queue (Spec 8.4, „einfach halten").
 *
 * **Eine** feste Struktur, kein Regel-Builder: bleibt ein Ticket `open` und
 * ohne Agent-Antwort laenger als `no_reply_hours`, steigt die Prioritaet auf
 * `raise_priority_to` und die hinterlegten Empfaenger werden benachrichtigt.
 * Jedes Ticket eskaliert hoechstens einmal (`tickets.escalated_at`).
 */
export const queueEscalationRuleSchema = z.object({
    enabled: z.boolean().default(false),
    no_reply_hours: z.number().int().min(1).max(720).default(24),
    raise_priority_to: ticketPrioritySchema.default('important'),
    /** Zusaetzlicher Empfaenger neben dem Zustaendigen; `null` = keiner. */
    notify_member_id: uuidSchema.nullable().default(null),
    notify_team_id: uuidSchema.nullable().default(null),
});
export const queueSettingsSchema = z.object({
    escalation: queueEscalationRuleSchema.nullable().default(null),
    due_hours: queueDueHoursSchema.nullable().default(null),
});
export const DEFAULT_QUEUE_SETTINGS = { escalation: null, due_hours: null };
/**
 * Settings einer gespeicherten Zeile lesen. Queues aus Phase 1 haben ein
 * leeres `{}` in der Spalte — ohne diesen Durchlauf waeren `escalation` und
 * `due_hours` dort zur Laufzeit `undefined`, obwohl der Typ sie verspricht.
 */
export function normalizeQueueSettings(value) {
    const parsed = queueSettingsSchema.safeParse(value ?? {});
    return parsed.success ? parsed.data : DEFAULT_QUEUE_SETTINGS;
}
/** Frist-Vorgabe fuer eine Prioritaet in Stunden — `null` = keine. */
export function dueHoursFor(settings, priority) {
    return settings.due_hours?.[priority] ?? null;
}
export const queueSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    name: z.string().min(1).max(200),
    settings: queueSettingsSchema,
    /** `null` = keine E-Mail-Queue (Spec 3.2). */
    inbound_config: inboundConfigSchema.nullable(),
    outbound_config: outboundConfigSchema.nullable(),
    /**
     * Gewaehlte Verbindungen (ADR 0017). `outbound_connection_id = null` heisst
     * **nicht** „kein Versand", sondern „die Standard-Mail-Verbindung des
     * Mandanten" — die Vererbung ist der ganze Sinn der Sache. Bei IMAP gibt es
     * keinen Default: ohne `inbound_connection_id` gibt es kein Postfach.
     */
    outbound_connection_id: uuidSchema.nullable(),
    inbound_connection_id: uuidSchema.nullable(),
    /** Welche Verbindung der Versand **tatsaechlich** benutzt (Queue → Default). */
    effective_outbound_connection_id: uuidSchema.nullable(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
export const createQueueSchema = z.object({
    name: z.string().min(1).max(200),
    settings: queueSettingsSchema.partial().optional(),
});
export const updateQueueSchema = createQueueSchema.partial();
/**
 * E-Mail-Konfiguration einer Queue. Eigener Endpunkt statt Teil von
 * `updateQueueSchema`, weil hier der Adress-Index plattformweit abgeglichen
 * wird und die IMAP-Zeitplaene nachgezogen werden muessen.
 *
 * Ein weggelassenes Feld bleibt unveraendert, `null` schaltet den Kanal ab
 * bzw. — bei den Verbindungen — nimmt die eigene Wahl zurueck.
 */
export const updateQueueEmailSchema = z.object({
    inbound_config: inboundConfigSchema.nullable().optional(),
    outbound_config: outboundConfigSchema.nullable().optional(),
    /** `null` = keine eigene Wahl, es gilt die Standard-Verbindung des Mandanten. */
    outbound_connection_id: uuidSchema.nullable().optional(),
    inbound_connection_id: uuidSchema.nullable().optional(),
});
//# sourceMappingURL=queue.js.map